import { inject, Injectable } from '@angular/core';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { GenericDynamicComponent } from 'src/app/core/classes/DynamicComponent';
import { PageComponentService, BuilderPageComponent } from 'src/app/core/interfaces/page-creator';
import { PAGE_COMPONENT_TYPES } from 'src/app/core/interfaces/page-creator/to-pandora-index';

@Injectable({
  providedIn: 'root',
})
export class DefaultPageComponentService implements PageComponentService<BuilderPageComponent> {
  #pageComponents: { [id: string]: () => Promise<GenericDynamicComponent> } = {};
  #logger = inject(GenericLogger);

  constructor() {
    this.#registerPageComponents();
  }

  async getComponent(id: string, pageComponent?: BuilderPageComponent): Promise<GenericDynamicComponent | undefined> {
    const loadComponentFn = this.#pageComponents[id];
    if (!loadComponentFn) {
      this.#logPageComponentError(id, pageComponent);
      return undefined;
    }

    const module = await loadComponentFn();
    this.#logger.debug({ msg: `Loaded page component of type: ${id}` });
    return module;
  }

  #logPageComponentError(id: string, pageComponent?: BuilderPageComponent) {
    const msg = `Attempt to load an unknown page component type, this is usually a unknown/misconfigured page component in the page-creator builder model. ID: ${id}`;
    this.#logger.error({ msg, data: pageComponent });
  }

  #addPageComponent(pageComponent: BuilderPageComponent, loadComponentFn: () => any): void {
    let id: string = pageComponent.id;
    if (this.#pageComponents[id]) {
      console.warn(`Registering Page Layout Component with repeated ID: ${id}`);
      return;
    }

    this.#pageComponents[id] = loadComponentFn;
  }

  #registerPageComponents() {
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.BUILDER_SECTION }, () =>
      import('../../../pages/default/builder-section-sdk-1/builder-section-sdk-1.component').then(
        (m) => m.AppBuilderSectionSdk1Component,
      ),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.BUILDER_SECTION_SDK_2 }, () =>
      import('../../../pages/default/builder-section-sdk-2/builder-section-sdk-2.component').then(
        (m) => m.AppBuilderSectionSdk2Component,
      ),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.PAGE_CONTAINER }, () =>
      import('../../../pages/default/page-container-sdk-1/page-container-sdk-1.component').then(
        (m) => m.AppPageContainerSdk1Component,
      ),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.PAGE_CONTAINER_SDK_2 }, () =>
      import('../../../pages/default/page-container-sdk-2/page-container-sdk-2.component').then(
        (m) => m.AppPageContainerSdk2Component,
      ),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.PAGE_TITLE }, () =>
      import('../../../pages/default/page-title/page-title.component').then((m) => m.AppPageTitleComponent),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.PRODUCT_CAROUSEL }, () =>
      import('../../../pages/product/product-carousel-container/product-carousel-container.component').then(
        (m) => m.AppProductCarouselContainerComponent,
      ),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.PRODUCT_GRID }, () =>
      import('../../../pages/product/product-grid/product-grid.component').then((m) => m.AppProductGridComponent),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.CATEGORY_BANNER }, () =>
      import('../../../shared/components/builder/category-banner/category-banner.component').then(
        (m) => m.AppCategoryBannerComponent,
      ),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.TIMER_BANNER }, () =>
      import('../../../shared/components/builder/timer/timer.component').then((m) => m.AppTimerComponent),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.SLIDESHOW }, () =>
      import('../../../shared/components/builder/home-top-slideshow/home-top-slideshow.component').then(
        (m) => m.AppHomeTopSlideshowComponent,
      ),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.CAROUSEL }, () =>
      import('../../../shared/components/builder/home-top-carousel/home-top-carousel.component').then(
        (m) => m.AppHomeTopCarouselComponent,
      ),
    );
    this.#addPageComponent({ id: PAGE_COMPONENT_TYPES.SUBMENU }, () =>
      import('../../../pages/default/submenu/submenu.component').then((m) => m.AppSubMenuComponent),
    );
  }
}
